import { NamedAndLeveled } from 'commonTypes'
import type { CompoundDetailsWithRelatedDataDomain } from 'compoundTypes'
import type { TableRowData } from 'tableTypes'
import { formatNumberToNumberWithSpecifiedDecimals } from 'utils/commonUtils'
import { sortByLevelAscending, sortByLevelDescending } from 'utils/mtcUtils'

/**
 * Sorts drivers
 *
 * @param {CompoundDetailsWithRelatedDataDomain} compound - Compound
 * @returns { NamedAndLeveled[] } - drivers sorted
 */
const sortDrivers = (
    compound: CompoundDetailsWithRelatedDataDomain
): NamedAndLeveled[] => {
    const drivers = compound.drivers ?? []
    return compound.level >= 0
        ? sortByLevelDescending<NamedAndLeveled>(drivers)
        : sortByLevelAscending<NamedAndLeveled>(drivers)
}

/**
 * Sorts targets
 *
 * @param {CompoundDetailsWithRelatedDataDomain} compound - Compound
 * @returns { NamedAndLeveled[] } - targets sorted
 */
const sortTargets = (
    compound: CompoundDetailsWithRelatedDataDomain
): NamedAndLeveled[] => {
    const targets = compound.targets ?? []
    return compound.level >= 0
        ? sortByLevelDescending(targets)
        : sortByLevelAscending(targets)
}

/**
 * Maps a CompoundDetailsWithRelatedDataDomain to table row data structure.
 *
 * @param {CompoundDetailsWithRelatedDataDomain} compound
 * @param {string} pathname - Translation function
 * @param {string => string} t - Translation function
 * @returns CompoundDetailsWithRelatedDataDomain mapped to table row data
 */
export const mappedCompoundToTableRowData = (
    compound: CompoundDetailsWithRelatedDataDomain,
    pathname: string,
    t: (key: string) => string
): TableRowData => {
    const sortedDrivers = sortDrivers(compound)

    const drivers = sortedDrivers
        ? sortedDrivers
              .map(
                  (d) =>
                      `${
                          d.name
                      } (GBS: ${formatNumberToNumberWithSpecifiedDecimals(
                          d.level,
                          2
                      )})`
              )
              .join(', ')
        : t('common:labels.na')

    const sortedTargets = sortTargets(compound)

    const targets = sortedTargets
        ? sortedTargets
              .map(
                  (target) =>
                      `${
                          target.name
                      } (GTS: ${formatNumberToNumberWithSpecifiedDecimals(
                          target.level,
                          2
                      )})`
              )
              .join(', ')
        : t('common:labels.na')

    return {
        cells: [
            {
                text: compound.name ?? '',
                linkPath: `${pathname}/${compound.linkPath}`
            },
            {
                text: drivers
            },
            {
                text: targets
            },
            {
                text: formatNumberToNumberWithSpecifiedDecimals(
                    compound.level,
                    2
                ),
                isNegative: compound.level < 0,
                isProgressBar: true
            }
        ]
    }
}
